import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowLeft, Bell, BellOff, ChevronDown, Hash, MessageSquare,
  Paperclip, Plus, Send, Users, X,
} from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@easyflow/ui-frontend/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@easyflow/ui-frontend/components/ui/avatar';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import { EmptyState } from '@/components/aurora/EmptyState';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { cn } from '@/lib/utils';
import { storageFileUrl } from '@/lib/storage-url';
import { apiClient, type ApiResponse } from '@/lib/api';

// Task #1722 — Desktop master-detail Channels (estratto dal monolite
// `ChannelsPage.tsx` ora dispatcher viewport-based). Tipizzato senza `any`
// e ripulito da dead-code/import inutilizzati dopo l'estrazione.

interface ChannelAttachment {
  filename?: string | null;
  name?: string | null;
  mime_type?: string | null;
  type?: string | null;
  cloud_path?: string | null;
  url?: string | null;
  size?: number | null;
}

interface ChannelSummary {
  id: string;
  title: string | null;
  description: string | null;
  is_subscribed?: boolean;
  is_public?: boolean;
  created_by?: string | null;
  member_count?: number | null;
  latest_post_at?: string | null;
  last_post_preview?: string | null;
  last_post_title?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
}

interface ChannelDetail extends ChannelSummary {
  my_role?: 'admin' | 'publisher' | 'member' | 'readonly' | null;
}

interface ChannelPost {
  id: string;
  content: string | null;
  attachments?: ChannelAttachment[] | string | null;
  sender_first_name?: string | null;
  sender_last_name?: string | null;
  sender_avatar_url?: string | null;
  created_at: string;
}

interface ChannelComment {
  id: string;
  content: string | null;
  first_name?: string | null;
  last_name?: string | null;
  avatar_url?: string | null;
  created_at: string;
}

function formatRelative(date: string): string {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const diffMins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffMins < 1) return 'ora';
  if (diffMins < 60) return `${diffMins} min fa`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} ore fa`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays} giorni fa`;
  return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
}

const CHANNEL_COLORS = [
  'from-blue-500 to-indigo-600', 'from-emerald-500 to-teal-600', 'from-purple-500 to-violet-600',
  'from-orange-500 to-red-600', 'from-pink-500 to-rose-600', 'from-cyan-500 to-blue-600',
  'from-amber-500 to-yellow-600', 'from-indigo-500 to-purple-600',
];

function getChannelColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return CHANNEL_COLORS[Math.abs(hash) % CHANNEL_COLORS.length] ?? CHANNEL_COLORS[0]!;
}

const AVATAR_GRADIENTS = [
  'from-blue-500 to-indigo-600', 'from-emerald-500 to-teal-600', 'from-purple-500 to-violet-600',
  'from-orange-500 to-red-600', 'from-pink-500 to-rose-600', 'from-cyan-500 to-blue-600',
];

function getAvatarGradient(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length] ?? AVATAR_GRADIENTS[0]!;
}

function parseAttachments(att: ChannelPost['attachments']): ChannelAttachment[] {
  if (!att) return [];
  if (Array.isArray(att)) return att;
  try {
    const parsed: unknown = JSON.parse(att);
    return Array.isArray(parsed) ? (parsed as ChannelAttachment[]) : [];
  } catch {
    return [];
  }
}

function attachmentUrl(a: ChannelAttachment): string {
  // Task #4040 — serving autenticato con token in query (asset endpoint).
  return storageFileUrl(a.cloud_path || a.url);
}

export default function ChannelsPageDesktop() {
  const { user } = useAuth();
  const [channels, setChannels] = useState<ChannelSummary[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<ChannelSummary | null>(null);
  const [posts, setPosts] = useState<ChannelPost[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [postText, setPostText] = useState('');
  const [postFiles, setPostFiles] = useState<File[]>([]);
  const postFileRef = useRef<HTMLInputElement>(null);
  const [newIsPublic, setNewIsPublic] = useState(true);
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const [commentTexts, setCommentTexts] = useState<Record<string, string>>({});
  const [comments, setComments] = useState<Record<string, ChannelComment[]>>({});
  const [channelConvDetail, setChannelConvDetail] = useState<ChannelDetail | null>(null);

  const loadChannels = useCallback(async () => {
    const res: ApiResponse<ChannelSummary[]> = await apiClient.get<ChannelSummary[]>('/messenger/channels');
    if (res.success && Array.isArray(res.data)) setChannels(res.data);
  }, []);

  useEffect(() => { void loadChannels(); }, [loadChannels]);

  const selectChannel = useCallback(async (ch: ChannelSummary) => {
    setSelectedChannel(ch);
    const res: ApiResponse<ChannelPost[]> = await apiClient.get<ChannelPost[]>(`/messenger/conversations/${ch.id}/messages`);
    if (res.success && Array.isArray(res.data)) setPosts([...res.data].reverse());
  }, []);

  const createChannel = async () => {
    if (!newName.trim()) return;
    const res = await apiClient.post('/messenger/conversations', {
      type: 'channel',
      title: newName,
      description: newDesc,
      is_public: newIsPublic,
    });
    if (res.success) {
      setShowCreate(false);
      setNewName('');
      setNewDesc('');
      setNewIsPublic(true);
      void loadChannels();
    }
  };

  const subscribe = async (chId: string) => {
    await apiClient.post(`/messenger/conversations/${chId}/subscribe`, {});
    void loadChannels();
  };

  const unsubscribe = async (chId: string) => {
    await apiClient.delete(`/messenger/conversations/${chId}/subscribe`);
    void loadChannels();
  };

  const publishPost = async () => {
    if ((!postText.trim() && postFiles.length === 0) || !selectedChannel) return;
    if (postFiles.length > 0) {
      const fd = new FormData();
      if (postText) fd.append('content', postText);
      postFiles.forEach((f) => fd.append('files', f));
      await apiClient.post(`/messenger/conversations/${selectedChannel.id}/messages`, fd);
    } else {
      await apiClient.post(`/messenger/conversations/${selectedChannel.id}/messages`, { content: postText });
    }
    setPostText('');
    setPostFiles([]);
    await selectChannel(selectedChannel);
  };

  const toggleComments = async (msgId: string) => {
    const next = new Set(expandedComments);
    if (next.has(msgId)) {
      next.delete(msgId);
    } else {
      next.add(msgId);
      if (!comments[msgId]) {
        const res: ApiResponse<ChannelComment[]> = await apiClient.get<ChannelComment[]>(`/messenger/messages/${msgId}/comments`);
        if (res.success && Array.isArray(res.data)) {
          setComments((prev) => ({ ...prev, [msgId]: res.data ?? [] }));
        }
      }
    }
    setExpandedComments(next);
  };

  const submitComment = async (msgId: string) => {
    const text = commentTexts[msgId];
    if (!text?.trim()) return;
    await apiClient.post(`/messenger/messages/${msgId}/comments`, { content: text });
    setCommentTexts((prev) => ({ ...prev, [msgId]: '' }));
    const res: ApiResponse<ChannelComment[]> = await apiClient.get<ChannelComment[]>(`/messenger/messages/${msgId}/comments`);
    if (res.success && Array.isArray(res.data)) {
      setComments((prev) => ({ ...prev, [msgId]: res.data ?? [] }));
    }
  };

  const isTenantAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  useEffect(() => {
    if (!selectedChannel) {
      setChannelConvDetail(null);
      return;
    }
    void (async () => {
      const res: ApiResponse<ChannelDetail> = await apiClient.get<ChannelDetail>(`/messenger/conversations/${selectedChannel.id}`);
      if (res.success && res.data) setChannelConvDetail(res.data);
    })();
  }, [selectedChannel?.id, selectedChannel]);

  if (selectedChannel) {
    const channelDetail = channels.find((c) => c.id === selectedChannel.id);
    const myRole = channelConvDetail?.my_role;
    const canPost = !!channelDetail?.is_subscribed
      && (myRole === 'admin' || myRole === 'publisher' || isTenantAdmin || selectedChannel.created_by === user?.id);

    return (
      <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-950">
        <div className="border-b bg-white dark:bg-gray-900 px-4 py-3 flex items-center gap-3 shadow-sm">
          <Button variant="ghost" size="icon" className="h-9 w-9 rounded-lg" onClick={() => setSelectedChannel(null)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className={`h-9 w-9 rounded-lg bg-gradient-to-br ${getChannelColor(selectedChannel.title || '')} flex items-center justify-center shadow-sm`}>
            <Hash className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-sm truncate">{selectedChannel.title}</h2>
            {selectedChannel.description && <p className="text-xs text-muted-foreground line-clamp-1">{selectedChannel.description}</p>}
          </div>
          <Badge variant="outline" className="text-xs gap-1 rounded-full px-3">
            <Users className="h-3 w-3" />{channelDetail?.member_count || 0} iscritti
          </Badge>
          {channelDetail?.is_subscribed ? (
            <Button variant="outline" size="sm" className="rounded-lg gap-1.5" onClick={() => unsubscribe(selectedChannel.id)}>
              <BellOff className="h-3.5 w-3.5" />Disiscriviti
            </Button>
          ) : (
            <Button size="sm" className="rounded-lg gap-1.5 bg-blue-600 hover:bg-blue-700" onClick={() => subscribe(selectedChannel.id)}>
              <Bell className="h-3.5 w-3.5" />Iscriviti
            </Button>
          )}
        </div>

        <ScrollArea className="flex-1 p-4">
          <div className="max-w-3xl mx-auto">
            {canPost && (
              <div className="bg-white dark:bg-gray-900 rounded-xl border shadow-sm mb-6 overflow-hidden">
                <div className="p-4">
                  <Textarea
                    placeholder="Pubblica un annuncio nel canale..."
                    value={postText}
                    onChange={(e) => setPostText(e.target.value)}
                    rows={3}
                    className="border-0 resize-none bg-gray-50 dark:bg-gray-800 rounded-lg"
                  />
                  {postFiles.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {postFiles.map((f, i) => (
                        <Badge key={i} variant="secondary" className="text-xs pr-1 rounded-full gap-1">
                          <Paperclip className="h-3 w-3" />
                          {f.name}
                          <button
                            type="button"
                            className="ml-1 hover:text-destructive rounded-full"
                            onClick={() => setPostFiles(postFiles.filter((_, j) => j !== i))}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex justify-between items-center px-4 py-2.5 border-t bg-gray-50 dark:bg-gray-800/50">
                  <div>
                    <input
                      type="file"
                      ref={postFileRef}
                      className="hidden"
                      multiple
                      onChange={(e) => {
                        if (e.target.files) setPostFiles([...postFiles, ...Array.from(e.target.files)]);
                        e.target.value = '';
                      }}
                    />
                    <Button variant="ghost" size="sm" className="gap-1.5 text-gray-500" onClick={() => postFileRef.current?.click()}>
                      <Paperclip className="h-3.5 w-3.5" />Allega file
                    </Button>
                  </div>
                  <Button size="sm" className="gap-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg px-5" onClick={publishPost} disabled={!postText.trim() && postFiles.length === 0}>
                    <Send className="h-3.5 w-3.5" />Pubblica
                  </Button>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {posts.map((post) => {
                const authorName = `${post.sender_first_name || ''} ${post.sender_last_name || ''}`.trim();
                const initials = `${(post.sender_first_name || 'U')[0]}${(post.sender_last_name || '')[0] || ''}`;
                const atts = parseAttachments(post.attachments);
                return (
                  <div key={post.id} className="bg-white dark:bg-gray-900 rounded-xl border shadow-sm overflow-hidden transition-all hover:shadow-md">
                    <div className="p-4">
                      <div className="flex items-start gap-3 mb-3">
                        <Avatar className="h-10 w-10 ring-2 ring-white dark:ring-gray-800 shadow-sm">
                          <AvatarImage src={post.sender_avatar_url ?? undefined} />
                          <AvatarFallback className={`bg-gradient-to-br ${getAvatarGradient(authorName)} text-white text-xs font-semibold`}>
                            {initials}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <span className="text-sm font-semibold">{authorName || 'Utente'}</span>
                          <p className="text-xs text-muted-foreground">{formatRelative(post.created_at)}</p>
                        </div>
                      </div>

                      {post.content && <p className="text-sm whitespace-pre-wrap leading-relaxed">{post.content}</p>}

                      {atts.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {atts.map((att, i) => {
                            const mimeType = att.mime_type || att.type || '';
                            const fileName = att.filename || att.name || 'File';
                            const fileUrl = attachmentUrl(att);
                            if (mimeType.startsWith('image/') && fileUrl) {
                              return <img key={i} src={fileUrl} alt={fileName} className="max-w-full rounded-lg max-h-48 object-cover" />;
                            }
                            return (
                              <a key={i} href={fileUrl || '#'} target="_blank" rel="noreferrer"
                                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-50 dark:bg-gray-800 rounded-lg text-xs hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                                <Paperclip className="h-3.5 w-3.5 text-gray-400" /><span className="truncate">{fileName}</span>
                              </a>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="border-t px-4 py-2.5">
                      <button
                        type="button"
                        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                        onClick={() => toggleComments(post.id)}
                      >
                        <MessageSquare className="h-3.5 w-3.5" />
                        {comments[post.id]?.length || 0} commenti
                        <ChevronDown className={cn('h-3 w-3 transition-transform duration-200', expandedComments.has(post.id) && 'rotate-180')} />
                      </button>
                    </div>

                    {expandedComments.has(post.id) && (
                      <div className="border-t px-4 py-3 space-y-3 bg-gray-50/50 dark:bg-gray-800/30">
                        {(comments[post.id] || []).map((c) => {
                          const cName = `${c.first_name || ''} ${c.last_name || ''}`.trim();
                          return (
                            <div key={c.id} className="flex items-start gap-2.5">
                              <Avatar className="h-7 w-7 mt-0.5 shrink-0">
                                <AvatarImage src={c.avatar_url ?? undefined} />
                                <AvatarFallback className={`bg-gradient-to-br ${getAvatarGradient(cName)} text-white text-[9px] font-semibold`}>
                                  {(c.first_name?.[0] || '') + (c.last_name?.[0] || '')}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <div className="bg-white dark:bg-gray-800 rounded-xl px-3 py-2 border border-gray-100 dark:border-gray-700">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold">{cName}</span>
                                    <span className="text-[10px] text-muted-foreground">{formatRelative(c.created_at)}</span>
                                  </div>
                                  {c.content && <p className="text-xs mt-0.5">{c.content}</p>}
                                </div>
                              </div>
                            </div>
                          );
                        })}

                        {channelDetail?.is_subscribed && (
                          <div className="flex items-center gap-2">
                            <Input
                              placeholder="Scrivi un commento..."
                              className="h-8 text-xs rounded-lg bg-white dark:bg-gray-800"
                              value={commentTexts[post.id] || ''}
                              onChange={(e) => setCommentTexts((prev) => ({ ...prev, [post.id]: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Enter') void submitComment(post.id); }}
                            />
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 shrink-0" onClick={() => submitComment(post.id)}>
                              <Send className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {posts.length === 0 && (
                <div className="bg-white dark:bg-gray-900 rounded-xl border shadow-sm">
                  <EmptyState
                    illustration="no-posts"
                    title="Nessun post ancora"
                    subtitle={canPost
                      ? 'Pubblica il primo annuncio in questo canale per dare il via alla conversazione.'
                      : 'Nessun annuncio pubblicato in questo canale al momento.'}
                  />
                </div>
              )}
            </div>
          </div>
        </ScrollArea>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-950 h-full overflow-y-auto overflow-x-hidden p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-sm">
              <Hash className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Canali</h1>
              <p className="text-muted-foreground text-xs">Annunci e comunicazioni aziendali</p>
            </div>
          </div>
          {isTenantAdmin && (
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
              <DialogTrigger asChild>
                <Button className="gap-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg"><Plus className="h-4 w-4" />Crea canale</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader><DialogTitle>Nuovo canale</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <Label className="text-sm font-medium">Nome del canale</Label>
                    <Input placeholder="es. Comunicazioni dalla Direzione" value={newName} onChange={(e) => setNewName(e.target.value)} className="mt-1.5" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Descrizione</Label>
                    <Textarea placeholder="Descrizione del canale..." value={newDesc} onChange={(e) => setNewDesc(e.target.value)} rows={3} className="mt-1.5" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Visibilita</Label>
                    <div className="flex gap-2 mt-1.5">
                      <Button
                        variant={newIsPublic ? 'default' : 'outline'}
                        size="sm"
                        className="rounded-lg gap-1.5"
                        onClick={() => setNewIsPublic(true)}
                      >
                        <Hash className="h-3.5 w-3.5" />Pubblico
                      </Button>
                      <Button
                        variant={!newIsPublic ? 'default' : 'outline'}
                        size="sm"
                        className="rounded-lg gap-1.5"
                        onClick={() => setNewIsPublic(false)}
                      >
                        <Users className="h-3.5 w-3.5" />Privato
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5">
                      {newIsPublic ? 'Tutti possono vedere e iscriversi al canale' : 'Solo i membri invitati possono accedere'}
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowCreate(false)}>Annulla</Button>
                  <Button onClick={createChannel} className="bg-blue-600 hover:bg-blue-700">Crea</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {channels.map((ch) => (
            <div
              key={ch.id}
              className="bg-white dark:bg-gray-900 rounded-xl border shadow-sm cursor-pointer hover:shadow-md transition-all duration-200 group overflow-hidden"
              onClick={() => selectChannel(ch)}
            >
              <div className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`h-11 w-11 rounded-xl bg-gradient-to-br ${getChannelColor(ch.title || '')} flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform`}>
                    <Hash className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{ch.title}</h3>
                    {ch.description && <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{ch.description}</p>}
                  </div>
                </div>

                {(ch.last_post_preview || ch.latest_post_at || ch.updated_at) && (
                  <div className="mb-2 px-2 py-1.5 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 line-clamp-1">
                      {ch.last_post_preview || ch.last_post_title || 'Ultimo aggiornamento'}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      {formatRelative(ch.latest_post_at || ch.updated_at || ch.created_at || '')}
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Users className="h-3.5 w-3.5" />
                    <span>{ch.member_count || 0} iscritti</span>
                  </div>
                  {ch.is_subscribed ? (
                    <Badge className="text-[10px] rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800" variant="outline">
                      Iscritto
                    </Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs rounded-full px-3"
                      onClick={(e) => { e.stopPropagation(); void subscribe(ch.id); }}
                    >
                      Iscriviti
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {channels.length === 0 && (
          <div className="bg-white dark:bg-gray-900 rounded-xl border shadow-sm">
            <EmptyState
              illustration="no-posts"
              title="Nessun canale disponibile"
              subtitle={isTenantAdmin
                ? 'Crea il primo canale per le comunicazioni aziendali.'
                : 'Non ci sono canali disponibili al momento.'}
            />
          </div>
        )}
      </div>
    </div>
  );
}
